'use client'

import React, { useEffect, useId, useMemo, useRef, useState } from "react";

function getPos(side, rect, offset) {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  switch (side) {
    case "right":
      return { top: cy, left: rect.right + offset, transform: "translateY(-50%)" };
    case "left":
      return { top: cy, left: rect.left - offset, transform: "translate(-100%, -50%)" };
    case "bottom":
      return { top: rect.bottom + offset, left: cx, transform: "translateX(-50%)" };
    case "top":
    default:
      return { top: rect.top - offset, left: cx, transform: "translate(-50%, -100%)" };
  }
}

function composeHandlers(userHandler, ourHandler) {
  return (e) => {
    userHandler?.(e);
    if (!e?.defaultPrevented) ourHandler?.(e);
  };
}

export function Tooltip({
  children,
  content,
  side = "right",
  sideOffset = 10,
  disabled = false,
  className = "",
  contentClassName = "",
}) {
  const id = useId();
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState(null);

  const update = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setStyle(getPos(side, rect, sideOffset));
  };

  useEffect(() => {
    if (!open) return;
    update();

    const onScroll = () => update();
    const onResize = () => update();

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, side, sideOffset]);

  const child = useMemo(() => {
    // Tooltip expects a SINGLE element child (<a> or <button>)
    if (!React.isValidElement(children)) return children;

    const props = children.props ?? {};

    return React.cloneElement(children, {
      ...props,
      ref: (node) => {
        triggerRef.current = node;
        const existingRef = children.ref;
        if (typeof existingRef === "function") existingRef(node);
        else if (existingRef && typeof existingRef === "object") existingRef.current = node;
      },
      "aria-describedby": !disabled && open ? id : props["aria-describedby"],
      onMouseEnter: composeHandlers(props.onMouseEnter, () => {
        if (!disabled) setOpen(true);
      }),
      onMouseLeave: composeHandlers(props.onMouseLeave, () => setOpen(false)),
      onFocus: composeHandlers(props.onFocus, () => {
        if (!disabled) setOpen(true);
      }),
      onBlur: composeHandlers(props.onBlur, () => setOpen(false)),
    });
  }, [children, disabled, open, id]);

  return (
    <>
      {child}

      {!disabled && open && style && (
        <div
          id={id}
          role="tooltip"
          className={[
            "fixed z-[9999] pointer-events-none",
            "transition-opacity duration-150",
            className,
          ].join(" ")}
          style={style}
        >
          <div
            className={[
              "rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-800 shadow-md",
              "dark:border-white/10 dark:bg-zinc-900 dark:text-zinc-100",
              contentClassName,
            ].join(" ")}
          >
            {content}
          </div>
        </div>
      )}
    </>
  );
}